import { useEffect, useState } from 'react';
import CashToCloseControls from './CashToCloseControls';
import encompassFields from '../../data/cashtoclose_form.json';
import { fieldReader, fieldWriter } from '../../services/loanApi';
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from '../../utils/loanEditLock';
import { getAccessToken } from "../../services/authApi";
import Toast from '../shared/Toast';
import Modal from '../shared/Modal';
import LoadingOverlay from '../shared/LoadingOverlay';
import { error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import "react-datepicker/dist/react-datepicker.css";

export default function CashToClose() {
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [loanid, setLoanid] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState('Loan saved successfully...');
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [skipFetch, setSkipFetch] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");
    const [initialSubmitDate, setInitialSubmitDate] = useState('');
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    loanObject.all().then(async function (loan) {
                        setLoanid(loan.id);

                        const tpo = loan?.tpo || {};
                        const submittedDate = tpo.initialSubmitDate || '';
                        setInitialSubmitDate(submittedDate);

                        await getToken(false);
                    });
                });
            } catch (error) {
                const errorCode = logError("COMMERCIAL_APP_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "CashToClose",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                console.error("Auth block Error:", error);
                setLoading(false);
            }
        } else {
            console.warn("elli.script.guest is not available.");
            setLoading(false);
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
            setSkipFetch(skipFieldLoad);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };

    const fetchFields = async () => {
        const fieldIds = [...new Set([
            ...encompassFields.fields.map(field => field.id)
        ])];

        setLoading(true);
        let locked = false;
        try {
            const data = await fieldReader(token, [...fieldIds, ...LOAN_EDIT_LOCK_FIELD_IDS], loanid);
            setFieldValues(data);
            locked = isLoanEditLocked(data);
            setEditLocked(locked);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            console.error("Field fetch failed", err);
        } finally {
            if ((initialSubmitDate && initialSubmitDate !== "//") || locked) {
                // Delay to ensure DOM is rendered
                setTimeout(() => {
                    makeFormReadOnly();
                }, 100); // adjust delay if needed
            }

            setLoading(false);
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            'input:not([type=hidden]):not([readonly]):not([data-force-edit])',
            'textarea:not([readonly]):not([data-force-edit])',
            'select:not([disabled]):not([data-force-edit])',
            'input[type=checkbox]:not([disabled]):not([data-force-edit])',
            'button:not([data-allow])'
        ];

        const elements = document.querySelectorAll(selectors.join(', '));

        elements.forEach(el => {
            // Skip borrower pair select and its icons
            if (
                el.id === 'borrower-pair' ||
                el.closest('#borrower-pair-wrapper') // wrap icons with this ID
            ) {
                return;
            }

            if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
                el.setAttribute('readOnly', true);
            } else {
                el.setAttribute('disabled', true);
            }

            el.classList.add('cursor-not-allowed');
        });
    };

    useEffect(() => {
        if (!token) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }
        fetchFields();
    }, [token]);

    // reads the current UI value for a given field id using your existing conventions
    const getUiFieldValue = (rawId, fieldMeta) => {
        const escapedId = rawId.replace(/\./g, '\\.');
        const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
        const noCheckbox = document.getElementById(`field-${rawId}-no`);

        // Pair of Yes/No checkboxes
        if (yesCheckbox && noCheckbox) {
            if (fieldMeta?.datatype === "YN") {
                return yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
            }
            return yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
        }

        // Multiple checkboxes using same id (checkbox group)
        const groupChecks = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
        if (groupChecks && groupChecks.length > 1) {
            const checked = [...groupChecks].find(cb => cb.checked);
            return checked?.value || "";
        }

        // Single input (text/select/single checkbox)
        const input = document.getElementById(`field-${rawId}`);
        if (!input) return "";

        if (input.type === 'checkbox') {
            // Keep compatibility with your writer logic
            if (fieldMeta?.datatype === "YN") return input.checked ? "true" : "false";
            if (fieldMeta?.datatype === "Y") return input.checked ? "true" : "";
            return input.checked ? (input.value || "true") : "";
        }

        if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
            // For Save, use the selected OPTION's value (you already do this in Save path)
            return input.options[input.selectedIndex]?.value || "";
        }

        let val = input.value || "";
        if (fieldMeta?.datatype === "money" && typeof val === "string") {
            val = val.replace(/,/g, "");
        }
        return val;
    };

    const handleSubmit = async (isRetry = false) => {

        const requiredFromEncompass = (encompassFields.fields || []).filter(f => f.required === true);
        const missingFromEncompass = requiredFromEncompass.filter(f => {
            const uiVal = getUiFieldValue(f.id, f);
            return (uiVal == null || uiVal.toString().trim() === "");
        });

        if (missingFromEncompass.length > 0) {
            const messages = [
                ...missingFromEncompass.map(f => `• ${f.name || `Field ${f.id}`}`)
            ];
            setWarningToastMessage(`Please fill all required fields:<br/> ${messages.join('<br/> ')}`);
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        setLoadingText('Saving...');
        setLoading(true);
        await Promise.resolve();


        const allFieldsForThisPage = encompassFields.fields.filter(f => f.isUpdate);

        const updatedFields = allFieldsForThisPage
            .map(f => {
                const rawId = f.id;
                const escapedId = rawId.replace(/\./g, '\\.');
                const fieldId = `field-${rawId}`;
                const fieldMeta = encompassFields.fields.find(field => field.id === rawId);

                const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
                const noCheckbox = document.getElementById(`field-${rawId}-no`);

                let value;
                if (yesCheckbox && noCheckbox) {
                    // Yes/No checkbox pair logic
                    if (fieldMeta?.datatype === "YN") {
                        value = yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
                    } else {
                        value = yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
                    }
                } else {
                    // Single checkbox or other inputs
                    const input = document.getElementById(fieldId);
                    if (input?.type === 'checkbox') {
                        if (fieldMeta?.datatype === "YN") {
                            value = input.checked ? "true" : "false";
                        }
                        else if (fieldMeta?.datatype === "Y") {
                            value = input.checked ? "true" : "";
                        }
                        else {
                            // Check if it's a checkbox group (multiple with same ID)
                            const checkboxes = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
                            if (checkboxes.length > 1) {
                                const checked = [...checkboxes].find(cb => cb.checked);


                                return {
                                    id: rawId,
                                    value: checked?.value || fieldValues[rawId] || ""
                                };
                            }

                            // Handle regular input (text, single checkbox, etc.)
                            const input = document.getElementById(`field-${rawId}`);
                            if (input) {
                                if (input.type === 'checkbox') {
                                    if (!input.checked && fieldValues[rawId]) {
                                        value = fieldValues[rawId];
                                    } else {
                                        value = input.checked ? input.value || "true" : "";
                                    }
                                } else {
                                    value = input.value;
                                }
                            } else if (fieldValues[rawId]) {
                                value = fieldValues[rawId];
                            } else {
                                value = "";
                            }
                        }
                    } else {
                        if (fieldMeta?.datatype === "string" && input?.tagName === "SELECT") {
                            value = input.options[input.selectedIndex]?.value || "";
                        } else {
                            value = input?.value || "";
                            if (fieldMeta?.datatype === "money" && typeof value === "string") {
                                value = value.replace(/,/g, "");
                            }
                        }
                    }
                }

                return {
                    id: rawId,
                    value
                };
            });

        try {
            await fieldWriter(token, loanid, updatedFields);

            setShowToastText('Cash-to-Close saved successfully...');
            setShowToast(true);

            setTimeout(() => setShowToast(false), 10000);

        } catch (error) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            if (error.response?.status === 401 && !isRetry) {
                console.warn('401 detected, regenerating token...');
                await getToken(true);
                return handleSubmit(true);
            }

            const json = error.response?.data || {};
            const title = determineErrorTitle(json);
            setErrorTitle(title);

            const mainDetail = json.details || "Submission failed.";
            const errorMessages = generateFieldErrorMessages(json.errors || [], updatedFields, encompassFields.fields);
            const fullMessage = `${mainDetail}${errorMessages ? "\n" + errorMessages : ""}`;

            setErrorMessage(fullMessage);
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }

    };

    const generateFieldErrorMessages = (errors, payload, fieldsJson) => {
        return errors.map((error) => {
            const match = error.summary && error.summary.match ? error.summary.match(/changes\[(\d+)\]\.value/) : null;
            if (match) {
                const index = parseInt(match[1], 10);
                const payloadField = payload[index];
                const fieldId = payloadField?.id;
                const fieldMeta = fieldsJson.find((field) => field.id === fieldId);
                const fieldName = fieldMeta?.name || `Field ID ${fieldId}`;
                return `${fieldName} (${fieldId}): ${error.details}`;
            }
            return error.details;
        }).join('\n');
    };

    const determineErrorTitle = (json) => {
        if (json.summary === "Conflict" && json.details?.includes("locked by another user")) {
            return "Loan Locked by Another User";
        }

        if (json.summary === "Bad Request" && Array.isArray(json.errors) && json.errors.length > 0) {
            return "Invalid Fields Detected";
        }

        return "Submission Error";
    };

    useEffect(() => {
        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            const hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");

                const vanishTimer = setTimeout(() => {
                    hideToast();
                }, 500);

                return () => clearTimeout(vanishTimer);
            }, 4000);

            return () => clearTimeout(hideTimer);
        }
    }, [showToast]);

    const handleFieldChange = (fieldId, value) => {
        setFieldValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const isReadOnly = (initialSubmitDate && initialSubmitDate !== "//") || editLocked;

    return (
        <>
            <LoadingOverlay loading={loading || isSaving} text={loadingText || 'Saving...'} />

            <Toast
                visible={toastVisible}
                type="success"
                message={showToastText}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showErrorToast}
                onClose={() => setShowErrorToast(false)}
                type="error"
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Cash to Close</h2>
                    <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="flex-grow">
                        </div>

                        <div className="flex gap-2 pt-0 pr-4 pb-4 pl-4">
                            <button
                                onClick={() => handleSubmit(false)}
                                className={`btn-base btn-save ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                                disabled={isReadOnly}
                            >
                                Save
                            </button>

                        </div>
                    </div>

                    <CashToCloseControls
                        Fields={encompassFields.fields}
                        fieldValues={fieldValues}
                        onFieldChange={handleFieldChange}
                    />


                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={() => handleSubmit(false)}
                            className={`btn-base btn-save ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                            disabled={isReadOnly}
                        >
                            Save
                        </button>

                    </div>
                </div>
            </div>
        </>
    );
}
